import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';
import AddMachineFlow from 'components/frank/AddMachineFlow.jsx';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const translate = (key) => t(`quote_builder_widget.${key}`);

// A machine reference field set to "a new machine" (UserUnitPriceAttributeField's VALUE_NEW) shows the new machine's
// name input.
const NEW_MACHINE = 'new-attribute';

// The machines of one kind (machineKey: printing_machine, hot_stamping_machine) the widget prices on. The owner adds a
// machine as they add a material -- materials first: only the machines the widget's materials allow are offered
// (supported_by_widget_materials). Each machine is shown as the Manufacturing Cost Calculator settings list shows it
// (SettingsUserUnitPriceAttributeList): its name and the same property pills, read through that machine's settings
// configuration. labels names the quote_builder_widget translation key of each text; machineLabel the machine's own
// name, which titles the added machines until the settings configuration arrives.
//
// With labels.createNew the owner can also create a machine here, and it is added to the widget at once -- as a
// material created in the widget is. As the settings page does, a machine whose settings tab has a library (a printing
// machine) is created as /mcc adds one (AddMachineFlow: pick it from the library or enter it manually, then its price);
// any other (a hot stamping machine, which is only its name) inside its price form. allowedTypesField names the list
// endpoint's field holding the types the widget can use, the only ones the library offers.
const WidgetMachinesSection = ({ machineKey, listUrl, listField, allowedTypesField, labels, machineLabel, onMachinesChange }) => {
  const [machines, setMachines] = useState([]);
  const [widgetMaterials, setWidgetMaterials] = useState(false);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [settingsConfiguration, setSettingsConfiguration] = useState(null);
  const [machineFormConfiguration, setMachineFormConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const addPopupRef = useRef();
  const createPopupRef = useRef();

  const fetchMachines = () => {
    GetRequest({
      url: listUrl,
      callback: (data) => {
        setMachines(data[listField]);
        setWidgetMaterials(data.widget_materials);
        if (allowedTypesField) {
          setAllowedTypes(data[allowedTypesField]);
        }
        setLoading(false);
      },
      // Any failure -- an error status, an expired session, a dropped connection -- ends the loading state.
      onError: () => {
        setLoading(false);
        setErrorMessage(translate(labels.failedToLoad));
      }
    });
  };

  const fetchConfigurations = () => {
    GetRequest({
      url: `/quote-builder/config/settings/user_unit_prices/${machineKey}`,
      useCache: true,
      callback: (data) => { setSettingsConfiguration(data); },
      onError: () => { setErrorMessage(translate(labels.failedToLoad)); }
    });
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${machineKey}/${machineKey}`,
      callback: (data) => { setMachineFormConfiguration(data); }
    });
  };

  useEffect(() => {
    fetchMachines();
    fetchConfigurations();
  }, []);

  const tabConfiguration = (settingsConfiguration?.tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute');

  const closeAddPopup = () => {
    setShowAddPopup(false);
    if (addPopupRef.current) {
      addPopupRef.current.style.display = 'none';
    }
  };

  const openCreatePopup = () => {
    closeAddPopup();
    setErrorMessage('');
    setShowCreatePopup(true);
  };

  // The saved price names its machine -- the new one, or one the owner chose instead -- which is added to the widget.
  const addPricedMachineToWidget = (priceId) => {
    closeCreatePopup();
    GetRequest({
      url: `/quote-builder/user_unit_prices/${priceId}`,
      callback: (price) => { updateWidgetStatus({ id: price[`user_unit_price_attribute.${machineKey}`] }, true); },
      onError: () => { setErrorMessage(translate(labels.failedToUpdate)); }
    });
  };

  const refreshMachines = () => {
    fetchMachines();
    if (onMachinesChange) {
      onMachinesChange();
    }
  };

  const closeCreatePopup = () => {
    setShowCreatePopup(false);
    setErrorMessage('');
    if (createPopupRef.current) {
      createPopupRef.current.style.display = 'none';
    }
  };

  const updateWidgetStatus = (machine, widgetEnabled) => {
    setUpdatingId(machine.id);
    setErrorMessage('');
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/${machine.id}/update-widget-status`,
      data: { widget_enabled: widgetEnabled ? 'true' : 'false' },
      callback: () => {
        setUpdatingId(null);
        closeAddPopup();
        fetchMachines();
        if (onMachinesChange) {
          onMachinesChange();
        }
      },
      400: (data) => {
        setUpdatingId(null);
        setErrorMessage(data?.error || translate(labels.failedToUpdate));
      },
      404: () => {
        setUpdatingId(null);
        setErrorMessage(translate(labels.failedToUpdate));
      },
      500: () => {
        setUpdatingId(null);
        setErrorMessage(translate('server_error'));
      }
    }).catch(() => {
      // PostRequest also rejects after running a status handler, whose message stays; a failure no handler covers (a
      // dropped connection) gets one.
      setUpdatingId(null);
      setErrorMessage((currentMessage) => currentMessage || translate(labels.failedToUpdate));
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  const addedMachines = machines.filter((machine) => machine.widget_enabled);
  const addableMachines = machines.filter((machine) => machine.supported_by_widget_materials && !machine.widget_enabled);

  // Materials first. The library offers only the types the widget can use, so with none there is nothing to create.
  const canCreateMachine = Boolean(
    labels.createNew && tabConfiguration && (allowedTypesField ? allowedTypes.length > 0 : widgetMaterials)
  );

  const noAddableMachinesMessage = () => {
    if (!widgetMaterials) { return translate(labels.noWidgetMaterials); }
    if (allowedTypesField && allowedTypes.length === 0) { return translate(labels.noWidgetPrintingTypes); }
    if (machines.length === 0) { return translate(labels.noMachines); }
    return translate(labels.noMachinesForMaterials);
  };

  // The settings list's machine row: the name and its property pills, the action on the right.
  const machineRow = (machine, action) => (
    <tr key={machine.id} data-testid="widget-machine">
      <td>
        <strong>{machine.name}</strong>
        {machine.widget_enabled && !machine.supported_by_widget_materials && (
          <div data-testid="widget-machine-not-used" style={{ color: '#dc3545', fontSize: '12px' }}>
            {translate(labels.notUsed)}
          </div>
        )}
        <AttributePropertyTags
          properties={machine.properties}
          listingProperties={tabConfiguration?.listing_properties}
          subFormAttributes={machineFormConfiguration?.create_sub_form?.attributes}
          sectionAttributes={settingsConfiguration?.attributes}
        />
      </td>
      <td>
        <div className="float-right" style={{ whiteSpace: 'nowrap' }}>{action}</div>
      </td>
    </tr>
  );

  const machinesTable = (title, rows) => (
    <table className="table table-striped table-bordered-outside" style={{ marginTop: '10px' }}>
      <tbody>
        <tr>
          <th colSpan="2">
            <div className="float-left">{title}</div>
          </th>
        </tr>
        {rows}
      </tbody>
    </table>
  );

  const renderAddedMachines = () => {
    if (addedMachines.length === 0) {
      return (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate(labels.noneAdded)}
        </div>
      );
    }

    return machinesTable(
      `${tabConfiguration ? t(tabConfiguration.label) : t(machineLabel)} (${addedMachines.length})`,
      addedMachines.map((machine) => machineRow(machine, (
        <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, false)}>
          <i className="fa fa-trash-o" aria-hidden="true"></i>{' '}
          {updatingId === machine.id ? translate('removing') : translate('remove')}
        </a>
      )))
    );
  };

  const renderAddPopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <h4>{translate(labels.add)}</h4>
      {renderErrorMessage()}
      {addableMachines.length === 0 ? (
        <p>{noAddableMachinesMessage()}</p>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {machinesTable(
            translate(labels.select),
            addableMachines.map((machine) => machineRow(machine, (
              <a role="button" style={{ cursor: 'pointer' }} onClick={() => updateWidgetStatus(machine, true)}>
                <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
                {translate('add_to_widget')}
              </a>
            )))
          )}
        </div>
      )}
      {canCreateMachine && (
        <>
          <hr />
          <button type="button" className="btn simple-btn" onClick={openCreatePopup}>
            <i className="fa fa-plus" aria-hidden="true"></i>{' '}
            {translate(labels.createNew)}
          </button>
        </>
      )}
    </div>
  );

  // In AddMachineFlow the new machine is added to the widget as soon as it exists, before its price. A machine entered
  // manually with a printing type the widget cannot use is refused, and the refusal shows above the flow.
  const renderCreatePopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      {renderErrorMessage()}
      {tabConfiguration.library_enabled ? (
        <AddMachineFlow
          machineKey={machineKey}
          tabConfig={tabConfiguration}
          allowedTypes={allowedTypes}
          onCopied={(newAttributeId) => updateWidgetStatus({ id: newAttributeId }, true)}
          onPriced={refreshMachines}
          onClose={closeCreatePopup}
        />
      ) : (
        <QuoteBuilderForm
          config={settingsConfiguration}
          showingState="new"
          context={{ [machineKey]: NEW_MACHINE }}
          afterSave={addPricedMachineToWidget}
          afterCancel={closeCreatePopup}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '10px', color: '#666' }}>
        {translate('loading')}
      </div>
    );
  }

  return (
    <div className={styles.fieldGroup}>
      <p style={{ color: '#666' }}>{translate(labels.information)}</p>
      {!showAddPopup && !showCreatePopup && renderErrorMessage()}
      {renderAddedMachines()}
      <button type="button" className="btn simple-btn" onClick={() => setShowAddPopup(true)}>
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {translate(labels.add)}
      </button>
      {showAddPopup && (
        <ContentPopup
          myRef={addPopupRef}
          autoOpen={true}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowAddPopup(false);
            setErrorMessage('');
          }}
        />
      )}
      {showCreatePopup && (
        <ContentPopup
          myRef={createPopupRef}
          autoOpen={true}
          content={renderCreatePopupContent()}
          cancelButton={false}
          beforeClose={closeCreatePopup}
        />
      )}
    </div>
  );
};

export default WidgetMachinesSection;
